/**
 * payrollDebugUtils.ts - Debug utilities and name generation for payroll
 *
 * Phase 2: Extracted module-level functions from payroll-management.tsx
 */

import { Employee } from '../contexts/AppContext.tsx';

export const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
export const DEBUG_TARGET_SALES_CALCULATION = IS_DEVELOPMENT; // 개발환경에서만 기본 활성화
export const DEBUG_PREFIX = '[🎯 순수목표매출 계산]';

/**
 * 디버깅 로그 활성화/비활성화 제어 함수
 * 개발자 콘솔에서 window.toggleTargetSalesDebug() 로 호출 가능
 */
const toggleTargetSalesDebug = () => {
  if (!IS_DEVELOPMENT) {
    console.warn(`${DEBUG_PREFIX} 프로덕션 환경에서는 디버깅을 활성화할 수 없습니다.`);
    return false;
  }

  const currentState = (window as any).DEBUG_TARGET_SALES_CALCULATION ?? DEBUG_TARGET_SALES_CALCULATION;
  (window as any).DEBUG_TARGET_SALES_CALCULATION = !currentState;
  return (window as any).DEBUG_TARGET_SALES_CALCULATION;
};

// 개발환경에서만 전역 함수 등록
if (IS_DEVELOPMENT) {
  (window as any).toggleTargetSalesDebug = toggleTargetSalesDebug;
}

// 환경별 디버깅 로그 함수들
export const debugLog = (message: string, data?: any, forceLog: boolean = false) => {
  const isDebugEnabled = IS_DEVELOPMENT && ((window as any).DEBUG_TARGET_SALES_CALCULATION ?? DEBUG_TARGET_SALES_CALCULATION);
  if (!isDebugEnabled && !forceLog) return;

  if (data) {
  } else {
  }
};

export const debugGroup = (title: string, callback: () => void) => {
  const isDebugEnabled = IS_DEVELOPMENT && ((window as any).DEBUG_TARGET_SALES_CALCULATION ?? DEBUG_TARGET_SALES_CALCULATION);
  if (!isDebugEnabled) {
    callback();
    return;
  }

  console.group(`${DEBUG_PREFIX} ${title}`);
  callback();
  console.groupEnd();
};

export const debugCalculationStep = (step: string, input: any, output: any, formula?: string) => {
  const isDebugEnabled = IS_DEVELOPMENT && ((window as any).DEBUG_TARGET_SALES_CALCULATION ?? DEBUG_TARGET_SALES_CALCULATION);
  if (!isDebugEnabled) return;

};

// 환경별 로깅 헬퍼 함수들
export const devLog = (...args: any[]) => {
  if (IS_DEVELOPMENT) {
    console.log(...args);
  }
};

// 직원 이름 관련 헬퍼 함수
const getBaseNameAndSuffix = (name: string): { baseName: string; suffix: string | null } => {
  if (!name) return { baseName: "", suffix: null };

  // 이름 끝에 알파벳 한 글자가 있는지 확인 (예: "홍길동A", "김철수B")
  const match = name.match(/^(.+?)([A-Z])$/);
  if (match) {
    return { baseName: match[1], suffix: match[2] };
  }
  return { baseName: name, suffix: null };
};

// 직원 이름에 알파벳 접미사를 추가하는 함수
export const generateUniqueName = (name: string, employees: Employee[]): string => {
  const { baseName } = getBaseNameAndSuffix(name);

  // 동일한 기본 이름을 가진 직원들 찾기
  const sameBaseNameEmployees = employees.filter(emp => {
    const { baseName: empBaseName } = getBaseNameAndSuffix(emp.name);
    return empBaseName === baseName;
  });

  // 중복이 없으면 원래 이름 그대로 사용
  if (sameBaseNameEmployees.length === 0) {
    return name;
  }

  // 현재 사용 중인 접미사들 확인
  const usedSuffixes = sameBaseNameEmployees.map(emp => {
    const { suffix } = getBaseNameAndSuffix(emp.name);
    return suffix;
  }).filter(Boolean) as string[];

  // 알파벳 A부터 시작해서 사용 가능한 첫 번째 접미사 찾기
  for (let i = 0; i < 26; i++) {
    const suffix = String.fromCharCode(65 + i); // 'A'(65)부터 'Z'(90)까지
    if (!usedSuffixes.includes(suffix)) {
      return `${baseName}${suffix}`;
    }
  }

  // 만약 모든 알파벳이 사용 중이라면 (극히 드문 케이스)
  return `${baseName}Z${sameBaseNameEmployees.length}`;
};
